/**
 * safeNextPath — validates the `next` redirect target after login.
 *
 * Only a same-origin path is accepted: one leading "/", never "//" or "/\"
 * (protocol-relative URLs leave the site), no scheme, no control or
 * whitespace characters. Percent-encoded slashes are decoded before the
 * check so "%2F%2Fevil" cannot slip through. Anything else returns null and
 * the caller falls back to the role's home. (RUI-14, open redirect)
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 2048) return null;
  let decoded = raw;
  try {
    // Decode twice to catch double-encoded "%252F%252F".
    decoded = decodeURIComponent(decodeURIComponent(raw));
  } catch {
    return null;
  }
  for (const candidate of [raw, decoded]) {
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f\s]/.test(candidate)) return null;
    if (!candidate.startsWith('/')) return null;
    if (candidate.startsWith('//') || candidate.startsWith('/\\')) return null;
    if (candidate.includes('\\')) return null;
  }
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}
